import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Loader2, PlayCircle, FileText, CheckCircle, XCircle, AlertCircle, Shield, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const QABotPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState('smoke');
  const [running, setRunning] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    const checkAdminAndFetch = async () => {
      try {
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        const token = localStorage.getItem('token');
        const res = await fetch(`${BACKEND_URL}/api/admin/check`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) {
          navigate('/dashboard');
          return;
        }
        const data = await res.json();
        if (!data.is_admin) {
          navigate('/dashboard');
          return;
        }
        fetchHistory();
      } catch (e) {
        navigate('/dashboard');
      }
    };
    checkAdminAndFetch();
  }, [user, navigate]);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/qa-bot/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch history');
      const data = await res.json();
      setHistory(data.runs || []);
    } catch (error) {
      console.error('Error fetching QA history:', error);
      toast.error('Failed to load QA history');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRunQA = async () => {
    if (!window.confirm(
      `Run QA Bot in ${mode.toUpperCase()} mode?\n\n` +
      `• Smoke: 1 question/scope (~2-3 min)\n` +
      `• Standard: 5 questions/scope (~5-8 min)\n` +
      `• Full: 20 questions/scope (~15-25 min)\n\n` +
      `The bot will test all 9 modules across P4, P5, P6.`
    )) return;

    setRunning(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/qa-bot/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ mode })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'QA run failed');
      }

      const result = await res.json();
      
      // Bot started in background
      if (result.status === 'running') {
        toast.success(`QA Bot started in ${mode} mode! Check the history table below for progress.`);
        
        // Start polling for completion
        const runId = result.run_id;
        const pollInterval = setInterval(async () => {
          await fetchHistory();
          
          // Check if this specific run is complete
          const currentHistory = await getCurrentHistory();
          const thisRun = currentHistory.find(r => r._id === runId);
          
          if (thisRun && thisRun.status !== 'running') {
            clearInterval(pollInterval);
            
            if (thisRun.status === 'completed') {
              toast.success(`QA Bot completed: ${thisRun.questions_generated} questions, ${thisRun.findings_count} findings`);
              
              // Auto-open report
              if (thisRun.report_path) {
                setTimeout(() => handleViewReport(runId), 500);
              }
            } else if (thisRun.status === 'failed') {
              toast.error('QA Bot run failed. Check the history for details.');
            } else if (thisRun.status === 'timeout') {
              toast.error('QA Bot run timed out after 10 minutes.');
            } else if (thisRun.status === 'error') {
              toast.error('QA Bot run encountered an error.');
            }
          }
        }, 5000); // Poll every 5 seconds
        
        // Stop polling after 15 minutes max
        setTimeout(() => clearInterval(pollInterval), 15 * 60 * 1000);
      } else {
        // Immediate completion (shouldn't happen with new async design)
        toast.success(`QA Bot completed: ${result.questions_generated} questions, ${result.findings_count} findings`);
      }
      
      // Refresh history immediately
      await fetchHistory();

    } catch (error) {
      console.error('QA run error:', error);
      toast.error(error.message || 'QA bot execution failed');
    } finally {
      setRunning(false);
    }
  };

  const getCurrentHistory = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const res = await fetch(`${BACKEND_URL}/api/qa-bot/history?limit=20`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.runs || [];
    } catch {
      return [];
    }
  };

  const handleViewReport = async (runId) => {
    const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    
    try {
      // Fetch report with auth header
      const res = await fetch(`${BACKEND_URL}/api/qa-bot/report/${runId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Failed to fetch report');
      }
      
      // Get HTML content and open in new tab via blob
      const htmlContent = await res.text();
      const blob = new Blob([htmlContent], { type: 'text/html' });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Failed to load report');
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return 'N/A';
    return new Date(isoString).toLocaleString('en-SG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'error':
        return <AlertCircle className="h-5 w-5 text-orange-500" />;
      default:
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-6 px-4 sm:px-6 shadow-lg">
        <div className="max-w-6xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin')}
            className="text-white hover:bg-white/20 mb-3"
            data-testid="qa-bot-back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Admin
          </Button>
          <div className="flex items-center gap-3">
            <Shield className="h-8 w-8" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">QA Bot</h1>
              <p className="text-sm text-indigo-100 mt-1">
                Test worksheet robustness across all modules and levels
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Run QA Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Card className="p-6 bg-white shadow-lg border-indigo-100" data-testid="qa-bot-run-card">
            <h2 className="text-xl font-bold text-slate-800 mb-4">Run New QA Check</h2>
            
            <div className="space-y-4">
              {/* Mode Selector */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Test Mode
                </label>
                <select
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  disabled={running}
                  className="w-full p-3 border border-slate-300 rounded-lg bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="qa-bot-mode-select"
                >
                  <option value="smoke">Smoke (1 question/scope, ~2-3 min) — Fastest</option>
                  <option value="standard">Standard (5 questions/scope, ~5-8 min) — Balanced</option>
                  <option value="full">Full (20 questions/scope, ~15-25 min) — Comprehensive</option>
                </select>
              </div>

              {/* Info Box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-blue-900 mb-2">What does the QA Bot check?</h3>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Question schema and structure</li>
                  <li>✓ Duplicate and near-duplicate detection</li>
                  <li>✓ MCQ correct answer validation</li>
                  <li>✓ Explanation quality and safety keywords</li>
                  <li>✓ Level, module, and subtopic accuracy</li>
                  <li>✓ API errors and generation latency</li>
                </ul>
              </div>

              {/* Run Button */}
              <Button
                onClick={handleRunQA}
                disabled={running}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white font-semibold py-3 text-base"
                data-testid="qa-bot-run-button"
              >
                {running ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Running QA Bot...
                  </>
                ) : (
                  <>
                    <PlayCircle className="h-5 w-5 mr-2" />
                    Run QA Bot ({mode})
                  </>
                )}
              </Button>
            </div>
          </Card>
        </motion.div>

        {/* History Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="mt-8"
        >
          <Card className="p-6 bg-white shadow-lg border-slate-200" data-testid="qa-bot-history-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800">QA Run History</h2>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchHistory}
                disabled={loadingHistory}
                className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                data-testid="qa-history-refresh-button"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingHistory ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>

            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No QA runs yet. Run your first check above!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Mode</th>
                      <th className="text-left py-3 px-4 font-semibold text-slate-700">Started</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Questions</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Findings</th>
                      <th className="text-right py-3 px-4 font-semibold text-slate-700">Errors</th>
                      <th className="text-center py-3 px-4 font-semibold text-slate-700">Report</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((run) => (
                      <tr
                        key={run._id}
                        className="border-b border-slate-100 hover:bg-slate-50 transition-colors"
                        data-testid={`qa-run-row-${run._id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {getStatusIcon(run.status)}
                            <span className="capitalize text-slate-700">{run.status}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <span className="inline-block px-2 py-1 bg-indigo-100 text-indigo-700 rounded text-xs font-medium uppercase">
                            {run.mode}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{formatDate(run.started_at)}</td>
                        <td className="py-3 px-4 text-right text-slate-700 font-medium">
                          {run.questions_generated || 0}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium ${
                            (run.findings_count || 0) > 0 ? 'text-orange-600' : 'text-green-600'
                          }`}>
                            {run.findings_count || 0}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`font-medium ${
                            (run.api_errors || 0) > 0 ? 'text-red-600' : 'text-slate-400'
                          }`}>
                            {run.api_errors || 0}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {run.status === 'running' ? (
                            <span className="text-blue-600 text-xs flex items-center justify-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Running...
                            </span>
                          ) : run.report_path && run.status === 'completed' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewReport(run._id)}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                              data-testid={`qa-view-report-${run._id}`}
                            >
                              <FileText className="h-4 w-4 mr-1" />
                              View
                            </Button>
                          ) : (
                            <span className="text-slate-400 text-xs">N/A</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default QABotPage;
